import { changed, noOp, type BrushSettings, type Command, type CommandExecution, type SampledBrushTip } from './commands';
import { invertMatrix, transformPoint } from './geometry';
import { createId, findLayer, layerDocumentTransform, type EditorDocument, type LayerId } from './model';
import { pointIsSelected, selectionBounds } from './selection';

export const BRUSH_PRESETS_EXTENSION = 'maipo:brush-presets';

export interface BrushPreset {
  id: string;
  name: string;
  settings: Omit<BrushSettings, 'color' | 'mode'>;
}

function finite(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(minimum, Math.min(maximum, value)) : fallback;
}

function validPreset(value: unknown): value is BrushPreset {
  return Boolean(value && typeof value === 'object' && typeof (value as Partial<BrushPreset>).id === 'string' && typeof (value as Partial<BrushPreset>).name === 'string' && (value as Partial<BrushPreset>).settings && typeof (value as Partial<BrushPreset>).settings === 'object');
}

function sampledTip(value: unknown): SampledBrushTip | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Partial<SampledBrushTip>;
  const width = typeof source.width === 'number' ? Math.round(source.width) : 0;
  const height = typeof source.height === 'number' ? Math.round(source.height) : 0;
  if (width < 1 || height < 1 || width > 512 || height > 512 || !Array.isArray(source.alpha) || source.alpha.length !== width * height) return undefined;
  return {
    width,
    height,
    alpha: source.alpha.map((entry) => Math.round(finite(entry, 0, 0, 255))),
  };
}

export function brushPresets(document: EditorDocument): BrushPreset[] {
  const source = document.extensions[BRUSH_PRESETS_EXTENSION];
  if (!Array.isArray(source)) return [];
  return source.filter(validPreset).map((preset) => {
    const tip = sampledTip(preset.settings.tip);
    return {
      id: preset.id,
      name: preset.name.slice(0, 100),
      settings: {
      size: finite(preset.settings.size, 42, 1, 1_000),
      hardness: finite(preset.settings.hardness, 0.7, 0, 1),
      opacity: finite(preset.settings.opacity, 1, 0.01, 1),
      flow: finite(preset.settings.flow, 0.8, 0.01, 1),
      spacing: finite(preset.settings.spacing, 0.12, 0.01, 5),
      angle: finite(preset.settings.angle, 0, -180, 180),
      roundness: finite(preset.settings.roundness, 1, 0.05, 1),
      scatter: finite(preset.settings.scatter, 0, 0, 2),
      count: Math.round(finite(preset.settings.count, 1, 1, 8)),
      texture: finite(preset.settings.texture, 0, 0, 1),
      pressureSize: Boolean(preset.settings.pressureSize),
      pressureOpacity: preset.settings.pressureOpacity !== false,
      sizeJitter: finite(preset.settings.sizeJitter, 0, 0, 1),
      opacityJitter: finite(preset.settings.opacityJitter, 0, 0, 1),
        ...(tip ? { tip } : {}),
      },
    };
  });
}

export function createBrushPreset(name: string, brush: BrushSettings): BrushPreset {
  const settings: Omit<BrushSettings, 'color' | 'mode'> = {
    size: brush.size,
    hardness: brush.hardness,
    opacity: brush.opacity,
    flow: brush.flow,
    spacing: brush.spacing,
    angle: brush.angle ?? 0,
    roundness: brush.roundness ?? 1,
    scatter: brush.scatter ?? 0,
    count: brush.count ?? 1,
    texture: brush.texture ?? 0,
    pressureSize: brush.pressureSize ?? false,
    pressureOpacity: brush.pressureOpacity ?? true,
    sizeJitter: brush.sizeJitter ?? 0,
    opacityJitter: brush.opacityJitter ?? 0,
    ...(brush.tip ? { tip: structuredClone(brush.tip) } : {}),
  };
  return { id: createId('brush'), name: name.trim().slice(0, 100) || 'Custom brush', settings };
}

function rasterAlphaAt(document: EditorDocument, layerId: LayerId, x: number, y: number): number {
  const layer = findLayer(document.root, layerId);
  if (!layer || layer.kind !== 'raster') return 0;
  const inverse = invertMatrix(layerDocumentTransform(document.root, layer.id) ?? layer.transform);
  if (!inverse) return 0;
  const local = transformPoint(inverse, { x, y });
  const pixelX = Math.floor(local.x);
  const pixelY = Math.floor(local.y);
  if (pixelX < 0 || pixelY < 0 || pixelX >= layer.raster.width || pixelY >= layer.raster.height) return 0;
  const tileX = Math.floor(pixelX / layer.raster.tileSize);
  const tileY = Math.floor(pixelY / layer.raster.tileSize);
  const pixels = layer.raster.getTile(tileX, tileY);
  if (!pixels) return 0;
  const dimensions = layer.raster.tileDimensions(tileX, tileY);
  const localX = pixelX - tileX * layer.raster.tileSize;
  const localY = pixelY - tileY * layer.raster.tileSize;
  const offset = (localY * dimensions.width + localX) * 4;
  const luminance = (pixels[offset] ?? 0) * 0.2126 + (pixels[offset + 1] ?? 0) * 0.7152 + (pixels[offset + 2] ?? 0) * 0.0722;
  return Math.round((pixels[offset + 3] ?? 0) * (1 - luminance / 255));
}

export function sampleBrushTip(document: EditorDocument, layerId: LayerId, maximumDimension = 128): SampledBrushTip | null {
  if (document.selection.inverted) return null;
  const bounds = selectionBounds(document.selection, { x: 0, y: 0, width: document.width, height: document.height });
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
  const scale = Math.min(1, Math.max(1, maximumDimension) / Math.max(bounds.width, bounds.height));
  const width = Math.max(1, Math.round(bounds.width * scale));
  const height = Math.max(1, Math.round(bounds.height * scale));
  const alpha = new Array<number>(width * height).fill(0);
  let used = false;
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const documentX = bounds.x + (x + 0.5) / width * bounds.width;
    const documentY = bounds.y + (y + 0.5) / height * bounds.height;
    if (!pointIsSelected(document.selection, { x: documentX, y: documentY })) continue;
    const coverage = rasterAlphaAt(document, layerId, documentX, documentY);
    alpha[y * width + x] = coverage;
    used ||= coverage > 0;
  }
  return used ? { width, height, alpha } : null;
}

export class SetBrushPresetsCommand implements Command {
  readonly label = 'Update brush presets';
  private before?: BrushPreset[];
  constructor(private readonly presets: readonly BrushPreset[]) {}
  retainedBytes(): number { return 256 + JSON.stringify(this.presets).length * 2 + JSON.stringify(this.before ?? []).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    this.before ??= brushPresets(document);
    if (JSON.stringify(this.before) === JSON.stringify(this.presets)) return noOp();
    if (this.presets.length) document.extensions[BRUSH_PRESETS_EXTENSION] = structuredClone(this.presets);
    else delete document.extensions[BRUSH_PRESETS_EXTENSION];
    return changed();
  }
  undo(document: EditorDocument): void {
    if (this.before?.length) document.extensions[BRUSH_PRESETS_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[BRUSH_PRESETS_EXTENSION];
  }
}
